/**
 * OR-6288 W5: the in-page "Create concept group" entry — replaces the modal
 * (flag-ON) for the asset list's Create menu. Renders the same stepper frame
 * as the draft review with step 1 active and step 2 disabled (no draft exists
 * yet); a successful upload mints the draft and navigates to its review, where
 * the full two-step flow takes over.
 *
 */

import { Stack, Step, StepButton, Stepper, Typography } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ConceptGroupDraftStepOne } from './ConceptGroupDraftStepOne';
import { useChatPreservingNavigate } from '../hooks/useChatPreservingNavigate';

export const ProjectConceptGroupCreatePage = () => {
  const { formatMessage } = useIntl();
  const { projectId = '' } = useParams<{ projectId: string }>();
  const chatNavigate = useChatPreservingNavigate();
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const goToList = () => chatNavigate(`/projects/${projectId}`);

  return (
    <Stack
      sx={{ height: '100%', minHeight: 0, overflow: 'auto', paddingX: 3, paddingY: 2, gap: 2 }}
      data-testid="cg-create-page"
    >
      <Typography variant="h5">
        {formatMessage({ id: 'epiChat.conceptGroupPreview.stepper.title' })}
      </Typography>
      <Stepper nonLinear activeStep={0} sx={{ width: '100%' }} data-testid="cg-create-stepper">
        <Step>
          <StepButton data-testid="cg-create-step-upload">
            {formatMessage({ id: 'epiChat.conceptGroupPreview.step.upload' })}
          </StepButton>
        </Step>
        <Step>
          {/* No draft yet — review unlocks once the upload mints one. */}
          <StepButton disabled data-testid="cg-create-step-review">
            {formatMessage({ id: 'epiChat.conceptGroupPreview.step.review' })}
          </StepButton>
        </Step>
      </Stepper>
      <ConceptGroupDraftStepOne
        previewId=""
        projectId={projectId}
        isUpload
        draftName=""
        conceptCount={null}
        sourceFilename={null}
        sourceUploadId={null}
        requireFile
        name={name}
        onNameChange={setName}
        file={file}
        onFileChange={setFile}
        onReturnToReview={goToList}
        onUploaded={({ previewId: newPreviewId }) =>
          // The legacy preview route forwards to the canonical asset route
          // once the draft's stable id resolves. (Previously interpolated the
          // whole created object — a `[object Object]` URL.)
          chatNavigate(`/projects/${projectId}/concept-group-preview/${newPreviewId}`)
        }
      />
    </Stack>
  );
};
